import { defineColor, defineColorHex } from './build';
import type { BeadColor } from './types';

/**
 * Catalogue maître des couleurs de perles.
 *
 * Chaque entrée a un `id` stable et globalement unique, référencé par la
 * quantification et la nomenclature (BOM) des kits. Le catalogue produit
 * vit côté Shopify (cf. types.ts, pivot e-commerce Lot N3).
 *
 * Sections :
 *  - DEMO_COLORS : 8 couleurs primaires sRGB exactes (tests + palette de démo)
 *  - HAMA_MIDI_COLORS : les couleurs OPAQUES achetables chez notre fournisseur
 *    (Ritohobby, article 205, sachet 6000). L'`id` EST le SKU fournisseur
 *    (`hama-205-XX`) → la palette colle à l'inventaire commandable et sert la
 *    nomenclature d'achat.
 *
 * ⚠️ Provenance des RGB (recherche vérifiée 07/2026) : HAMA NE PUBLIE AUCUN
 * hex officiel — seulement des nuanciers photo (numéro + nom). Les valeurs
 * ci-dessous sont le consensus de deux bases communautaires numérisées
 * concordantes (bitbead.app, pixel-beads.com, maxcleme/beadcolors), croisé
 * par une revue adversariale. Ce sont donc les meilleures approximations sRGB
 * disponibles, PAS une mesure colorimétrique de la perle physique. Calibrage
 * définitif possible plus tard en photographiant les sachets réels sous
 * lumière neutre. Les couleurs marquées « à vérifier » ont un conflit
 * nom↔teinte connu (le nom fournisseur ne décrit pas la teinte réelle).
 *
 * Exclus volontairement (inutilisables en mosaïque photo) : transparents,
 * néon/fluo, phosphorescents, pailletés, et le sachet « Mix ».
 */

export const DEMO_COLORS: ReadonlyArray<BeadColor> = [
  defineColor({ id: 'demo-k', name: 'Noir', rgb: { r: 0, g: 0, b: 0 } }),
  defineColor({ id: 'demo-w', name: 'Blanc', rgb: { r: 255, g: 255, b: 255 } }),
  defineColor({ id: 'demo-r', name: 'Rouge', rgb: { r: 255, g: 0, b: 0 } }),
  defineColor({ id: 'demo-g', name: 'Vert', rgb: { r: 0, g: 255, b: 0 } }),
  defineColor({ id: 'demo-b', name: 'Bleu', rgb: { r: 0, g: 0, b: 255 } }),
  defineColor({ id: 'demo-c', name: 'Cyan', rgb: { r: 0, g: 255, b: 255 } }),
  defineColor({ id: 'demo-m', name: 'Magenta', rgb: { r: 255, g: 0, b: 255 } }),
  defineColor({ id: 'demo-y', name: 'Jaune', rgb: { r: 255, g: 255, b: 0 } }),
];

const VERIFY = 'Conflit nom↔teinte connu (nom fournisseur ≠ teinte réelle) — à vérifier sur perle physique.';

/**
 * Couleurs Hama Midi OPAQUES achetables (Ritohobby art. 205, numéros HAMA).
 * IDs = SKU fournisseur `hama-205-${num}`. Ordre STABLE (index figé dans les
 * patrons sérialisés) : solides, puis pastels, puis nouveautés 2024-2025.
 */
export const HAMA_MIDI_COLORS: ReadonlyArray<BeadColor> = [
  // — Solides —
  defineColorHex({ id: 'hama-205-01', name: 'Blanc', hex: '#ECEDED' }),
  defineColorHex({ id: 'hama-205-02', name: 'Crème', hex: '#F0E8B9' }),
  defineColorHex({ id: 'hama-205-04', name: 'Orange', hex: '#E64F27' }),
  defineColorHex({ id: 'hama-205-05', name: 'Rouge', hex: '#B63136' }),
  defineColorHex({ id: 'hama-205-06', name: 'Rose', hex: '#E1889F' }),
  defineColorHex({ id: 'hama-205-07', name: 'Violet', hex: '#694A82' }),
  defineColorHex({ id: 'hama-205-08', name: 'Bleu', hex: '#2C4690' }),
  defineColorHex({ id: 'hama-205-09', name: 'Bleu clair', hex: '#305CB0' }),
  defineColorHex({ id: 'hama-205-10', name: 'Vert', hex: '#256847' }),
  defineColorHex({ id: 'hama-205-11', name: 'Vert clair', hex: '#49AE89' }),
  defineColorHex({ id: 'hama-205-12', name: 'Marron', hex: '#534137' }),
  defineColorHex({ id: 'hama-205-17', name: 'Gris', hex: '#83888A' }),
  defineColorHex({ id: 'hama-205-18', name: 'Noir', hex: '#232426' }),
  defineColorHex({ id: 'hama-205-20', name: 'Bordeaux', hex: '#7F332A', notes: VERIFY }),
  defineColorHex({ id: 'hama-205-21', name: 'Marron clair', hex: '#A5693F' }),
  defineColorHex({ id: 'hama-205-27', name: 'Beige', hex: '#DEB48B' }),
  defineColorHex({ id: 'hama-205-28', name: 'Vert foncé', hex: '#363F38', notes: VERIFY }),
  defineColorHex({ id: 'hama-205-29', name: 'Lie-de-vin', hex: '#B9395E', notes: VERIFY }),
  defineColorHex({ id: 'hama-205-30', name: 'Rouge foncé', hex: '#592F38', notes: VERIFY }),
  defineColorHex({ id: 'hama-205-31', name: 'Turquoise', hex: '#6797AE', notes: VERIFY }),
  defineColorHex({ id: 'hama-205-60', name: 'Jaune soleil', hex: '#F49422', notes: VERIFY }),
  defineColorHex({ id: 'hama-205-70', name: 'Gris clair', hex: '#B6B6D4', notes: VERIFY }),
  defineColorHex({ id: 'hama-205-71', name: 'Gris foncé', hex: '#464541' }),
  defineColorHex({ id: 'hama-205-75', name: 'Marron moyen', hex: '#BF7B4D' }),
  defineColorHex({ id: 'hama-205-76', name: 'Marron chocolat', hex: '#663317' }),
  defineColorHex({ id: 'hama-205-77', name: 'Blanc cassé', hex: '#EDE7DF' }),
  defineColorHex({ id: 'hama-205-78', name: 'Pêche clair', hex: '#FFC99A' }),
  defineColorHex({ id: 'hama-205-79', name: 'Abricot', hex: '#F08643' }),
  defineColorHex({ id: 'hama-205-82', name: 'Prune', hex: '#962F5C' }),
  defineColorHex({ id: 'hama-205-83', name: 'Essence', hex: '#0178A4' }),
  defineColorHex({ id: 'hama-205-84', name: 'Olive', hex: '#8B924C' }),
  // — Pastels (opaques) —
  defineColorHex({ id: 'hama-205-43', name: 'Jaune pastel', hex: '#F0EA37' }),
  defineColorHex({ id: 'hama-205-44', name: 'Rouge pastel', hex: '#EE6972' }),
  defineColorHex({ id: 'hama-205-46', name: 'Bleu pastel', hex: '#629ED7' }),
  defineColorHex({ id: 'hama-205-47', name: 'Vert pastel', hex: '#83CB70' }),
  defineColorHex({ id: 'hama-205-48', name: 'Rose pastel', hex: '#CF70B7' }),
  defineColorHex({ id: 'hama-205-49', name: 'Azur', hex: '#4998BC' }),
  defineColorHex({ id: 'hama-205-95', name: 'Rose tendre', hex: '#F8CCE0' }),
  defineColorHex({ id: 'hama-205-96', name: 'Violet pastel', hex: '#D4B1E3' }),
  defineColorHex({ id: 'hama-205-97', name: 'Bleu glacé', hex: '#A2D3FE' }),
  defineColorHex({ id: 'hama-205-98', name: 'Menthe pastel', hex: '#9ADBB1' }),
  // — Nouveautés 2024-2025 —
  defineColorHex({ id: 'hama-205-101', name: 'Eucalyptus', hex: '#A9C39B' }),
  defineColorHex({ id: 'hama-205-102', name: 'Vert forêt', hex: '#356B2D' }),
  defineColorHex({ id: 'hama-205-103', name: 'Jaune clair', hex: '#FFE660' }),
  defineColorHex({ id: 'hama-205-104', name: 'Citron vert', hex: '#BCD122' }),
  defineColorHex({ id: 'hama-205-105', name: 'Abricot clair', hex: '#FFAC78' }),
  defineColorHex({ id: 'hama-205-106', name: 'Lavande claire', hex: '#CCC5ED' }),
  defineColorHex({ id: 'hama-205-107', name: 'Lavande', hex: '#6A87C1' }),
  defineColorHex({ id: 'hama-205-110', name: 'Matcha', hex: '#838956' }),
  defineColorHex({ id: 'hama-205-112', name: 'Blush', hex: '#AD8A82' }),
  defineColorHex({ id: 'hama-205-113', name: 'Aqua', hex: '#5F887B' }),
  defineColorHex({ id: 'hama-205-114', name: 'Rouge cerise', hex: '#9A2C31' }),
  defineColorHex({ id: 'hama-205-115', name: 'Vert vif', hex: '#6E975F' }),
  defineColorHex({ id: 'hama-205-116', name: 'Bleu nuit', hex: '#222838' }),
];

/**
 * Ids NEUTRES (du plus clair au plus sombre) — sous-ensemble utilisé pour
 * garantir le style « Noir & blanc » (aucune perle colorée, cf. presets.ts).
 * On écarte « Gris clair » 70 (reflet lilas marqué) pour rester purement
 * neutre. ⚠️ Doit référencer des ids présents dans HAMA_MIDI_COLORS.
 */
export const NEUTRAL_COLOR_IDS: ReadonlyArray<string> = [
  'hama-205-01', // Blanc
  'hama-205-17', // Gris
  'hama-205-71', // Gris foncé
  'hama-205-18', // Noir
];

/** Toutes les couleurs du catalogue, prêtes à être indexées. */
export const ALL_COLORS: ReadonlyArray<BeadColor> = [...DEMO_COLORS, ...HAMA_MIDI_COLORS];
